import { useEffect, useRef, useState } from 'react'

import { signIn } from 'next-auth/react';

import styles from '../../styles/Login.module.scss'

import LoadingSpinner from '../../components/Account/LoadingSpinner/LoadingSpinner'

import { useRouter } from 'next/router'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark, faEnvelopeCircleCheck, faEye, faEyeSlash, faHome, faPlusCircle } from '@fortawesome/free-solid-svg-icons';

import axios from 'axios';

import { useSession } from 'next-auth/react'

import errorMsg from '../../components/Functions/errorMsg';
import Head from 'next/head';

export default function Login() {
  const router = useRouter();
  const {data: session, status} = useSession();

  const [noAuth, setNoAuth] = useState(false);

  useEffect(() => {
    if(noAuth === true) return;
    setNoAuth(true);
    if(status === 'authenticated') {
      router.push('/');
    }
  }, [status, noAuth])

  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  const [pendingRequest, setPendingRequest] = useState(false);

  const submitLogin = async (e) => {
    e.preventDefault();
    setPendingRequest(true)
    if(loginUsername.length <= 5) {
      errorMsg("Usernames are 6 characters more... please try again.");
      setPendingRequest(false);
      return;
    }
    if(loginPassword.length <= 6) {
      errorMsg("Passwords are 7 characters more... please try again.");
      setPendingRequest(false);
      return;
    }

    setShowPassword(false);

    const res = await signIn("credentials", {
      redirect: false,
      username: loginUsername,
      password: loginPassword,
    })

    if(res === undefined) {
      errorMsg("There was an internal error when logging in, please try again later.");
      setPendingRequest(false);
    }
    
    if(res.error === null) {
    if(router.query === null || router.query === undefined) return;
      const query = router.query;

      const account = query.account;

      if(account === 'eventAccount') {
        router.push({
          pathname: "/",
          query: {
            account: 'eventAccount'
          }
        })
      } else {
        router.push('/')
      }

    } else {
      setPendingRequest(false);
      if(res.error === 'ERROR_LOGIN_NO-USER') {
        errorMsg("There is no such user that exists in our database.")
      } else if (res.error === 'ERROR_LOGIN_CREDENTIALS') {
        errorMsg("Invalid username and password combination.");
      } else if (res.error === 'ERROR_LOGIN_BANNED') {
        errorMsg("You have been banned from using Waco Exchange. If you believe this is a mistake, please contact us.");
      } else if (res.error === 'ERROR_LOGIN_IP_ADDRESS') {
        errorMsg("Invalid network request, please turn off any VPNs or proxies. If the issue persists, please contact contact@wacoexchange.com");
      } else if (res.error.includes('NOT_VERIFIED_')) {
        router.push('/account/verify/' + res.error.replace('NOT_VERIFIED_', ''))
      } else if (res.error === 'CredentialsSignin') {
        errorMsg("There was an error connecting to the database.");
      } else {
        errorMsg(res.error);
      }
    }

  }

  const [showResetPass, setShowResetPass] = useState(false);
  const [pendingReset, setPendingReset] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  const resetUserRef = useRef(null);
  const resetEmailRef = useRef(null);

  const submitPassReset = async (e) => {
    e.preventDefault()
    if(pendingReset === true) return;
    setPendingReset(true);

    if(resetUserRef.current.value === '' || resetUserRef.current.value === null) {
      errorMsg('Please enter the username associated with your account.');
      setPendingReset(false);
      return;
    }

    if(resetEmailRef.current.value === '' || resetEmailRef.current.value === null) {
      errorMsg('Please enter the email associated with your account.');
      setPendingReset(false);
      return;
    }

    const result = await axios.post(`${window.location.origin}/api/account/generateResetPassword`, {
      requestingUsername: resetUserRef.current.value,
      requestingEmail: resetEmailRef.current.value,
      windowLocation: window.location.origin,
      creationDate: new Date()
    }).catch((error) => {
        if(error?.response?.data?.status !== undefined) {
          errorMsg(error?.response?.data?.status);
          setPendingReset(false);
        } else {
          errorMsg(error);
          setPendingReset(false);
        }
    })

    if(result !== undefined) {
        if(result.status === 200) {
          setLinkSent(true);
          setPendingReset(false);
        } else {
          setPendingReset(false);
          errorMsg('There was a problem verifying your account, please try again later.');
        }
    }

  }

  const [showPassword, setShowPassword] = useState(false);

  return (
    <section id={styles.loginWrapper}>
      <FontAwesomeIcon onClick={() => router.push('/')} icon={faHome} id={styles.goHome} />
      <Head>
        <title>Waco Exchange - Login</title>
        <meta name="description" content="Login to your Waco Exchange account. Start browsing and posting in our expanding list of marketplaces like buying/selling items, real-estate, events and more." key="desc" />

        <meta property="og:title" content="Waco Exchange - Login" />
        <meta property="og:description" content="Login to your Waco Exchange account. Start browsing and posting in our expanding list of marketplaces like buying/selling items, real-estate, events and more." />
      </Head>

      <form onSubmit={submitLogin} className={styles.loginWrapper}>
        <div>
          <span>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 1024" preserveAspectRatio="meet"><polygon points="163 0 0 0 0 88.61 34 121 163 0"/><polygon points="154 116 163 0 34 121 154 116"/><polygon points="413.05 0 163 0 348 67 413.05 0"/><polygon points="348 67 448 102 448 0 413.05 0 348 67"/><polygon points="312 231 448 210 448 102 348 67 312 231"/><polygon points="348 67 163 0 154 116 348 67"/><polygon points="312 231 348 67 154 116 312 231"/><polygon points="154 116 34 121 45.77 233.64 154 116"/><polygon points="48 231 312 231 154 116 48 231"/><polygon points="0 88.61 0 160 34 121 0 88.61"/><polygon points="34 121 0 157.86 0 330.37 62 389 34 121"/><polygon points="62 389 312 231 45.49 231 62 389"/><polygon points="201.18 411.36 312 231 209.23 295.95 201.18 411.36"/><polygon points="201.67 407.94 448 412 312 231 201.67 407.94"/><polygon points="448 412 448 210 312 231 448 412"/><polygon points="197 525 448 412 201.67 407.94 197 525"/><polygon points="62 389 197 525 209.23 295.95 62 389"/><polygon points="0 330.37 0 411.36 62 389 0 330.37"/><polygon points="27 605 197 525 62 389 27 605"/><polygon points="62 389 0 410.48 0 570.13 27 605 62 389"/><polygon points="122 786 197 525 27 605 122 786"/><polygon points="306.26 696.61 391 655.5 197 525 122 786 306.26 696.61"/><polygon points="391 655.5 448 412 197 525 391 655.5"/><polygon points="448 655.5 448 412 391 655.5 448 655.5"/><polygon points="448 837 448 655.5 391 655.5 448 837"/><polygon points="306.26 696.61 279 938 448 837 306.26 696.61"/><polygon points="306.26 696.61 448 837 391 655.5 306.26 696.61"/><polygon points="279 938 306.26 696.61 122 786 279 938"/><polygon points="122 786 0 740.91 0 861.44 16.25 913 122 786"/><polygon points="27 605 0 624.2 0 740.91 122 786 27 605"/><polygon points="0 570.13 0 624.2 27 605 0 570.13"/><polygon points="16.25 913 279 938 122 786 16.25 913"/><polygon points="0 952.43 0 1024 166 1024 16.25 913 0 952.43"/><polygon points="0 861.44 0 952.43 16.25 913 0 861.44"/><polygon points="16.25 913 162.7 1024 214.13 1024 279 938 16.25 913"/><polygon points="214.13 1024 448 1024 279 938 214.13 1024"/><polygon points="448 1024 448 837 279 938 448 1024"/></svg>
          </span>
          <section><h1>User Login</h1></section>
          <section><input disabled={pendingRequest === true ? true : false} type="text" placeholder="Username" onChange={e => setLoginUsername(e.target.value)} value={loginUsername} /></section>
          <section>
            <input disabled={pendingRequest === true ? true : false} type={showPassword === false ? 'password' : 'text'} placeholder="Password" onChange={e => setLoginPassword(e.target.value)} value={loginPassword} />
            <span disabled={pendingRequest === true ? true : false} onClick={() => setShowPassword(state => !state) }>
              {
                showPassword === true ? <FontAwesomeIcon icon={faEyeSlash} id={styles.icon} /> : <FontAwesomeIcon icon={faEye} id={styles.icon} />
              }
            </span>
          </section>
          <section> { pendingRequest === true ? <LoadingSpinner width="35px" height="35px" color="#154734" /> : <button type='submit'>Login</button> } </section>
          <section><h6 onClick={() => setShowResetPass(true)}>I forgot my password</h6></section>
          <hr></hr>
          <section onClick={() => router.push('/account/register')}><FontAwesomeIcon icon={faPlusCircle} id={styles.icon} /><h6>Create an account</h6></section>
        </div>
      </form>
      {
        showResetPass === true ?
        <div className={styles.resetPassword}>
          <FontAwesomeIcon onClick={() => { if(pendingRequest === false) setShowResetPass(false)}} icon={faCircleXmark} className={styles.exitReset} />
          <div>
            {
              linkSent === true ?
              <>
                <FontAwesomeIcon icon={faEnvelopeCircleCheck} color="#154734" size="4x" style={{marginBottom: '12px'}} />
                <h2>Link sent successfully!</h2>
                <p>If you don't see the link in your inbox, please check your spam inbox.</p>
              </>
              :
              <>
                <h1>Reset Password</h1>
                <h2>Username on account</h2>
                <input disabled={pendingReset === true ? true : false} ref={resetUserRef} type='text' placeholder="johndoe" autoComplete="off" />
                <h2>Email on account</h2>
                <input disabled={pendingReset === true ? true : false} ref={resetEmailRef} type='email' placeholder="johndoe@gmail.com" autoComplete="off" />
                <button onClick={(e) => submitPassReset(e)}>
                  {
                    pendingReset === true ?
                    <LoadingSpinner width="22px" height="22px" color="#FFFFFF" />
                    :
                    'Request Link'
                  }
                </button>
                <p>You'll receive a password reset link to the email on your account. <br /><br />
                  If you cannot remember your account information, please contact us at wacoexchange@gmail.com</p>
              </>
            }

          </div>
        </div>
        :
        null
      }

    </section>
  )
}
